import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../../../../hooks/useAuth';
import { formatPhoneNumber } from '../../../../../utils/formatNumber';
import {
    capitalizeFirstLetter,
    formatName,
    toLowerCase,
    toUpperCase
} from '../../../../../utils/formatText';

import InfoBox from '../../../../../components/minside/InfoBox';

import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useScrollToTop } from '../../../../../hooks/useScrollToTop';

function ProfileField({ label, value, action, loading = false }) {
    return (
        <div className="profile-field">
            <dt className="profile-field__label">{label}</dt>
            <dd className="profile-field__value">
                {loading ? (
                    <span>Laster...</span>
                ) : (
                    <>
                        <span>{value || 'Ikke oppgitt'}</span>
                        {action && <span className="profile-field__action">{action}</span>}
                    </>
                )}
            </dd>
        </div>
    );
}

function ProfileLink({ label, href, text, external = false, loading = false }) {
    return (
        <div className="profile-field">
            <dt className="profile-field__label">{label}</dt>
            <dd className="profile-field__value">
                {loading ? (
                    <span>Laster...</span>
                ) : (
                    <a
                        href={href}
                        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    >
                        {text}
                    </a>
                )}
            </dd>
        </div>
    );
}

function ProfileTab() {
    const { user, logout, loading } = useAuth();
    const navigate = useNavigate();
    const scrollToTop = useScrollToTop();

    const profileLoading = loading || !user;

    const handleLogout = async () => {
        await logout();
        navigate('/login', { replace: true });
        scrollToTop('smooth');
    };

    return (
        <div className="profile-tab">
            <section className="profile-section">
                <h4 className="profile-section__heading">Kontaktinformasjon</h4>
                <dl className="profile-list">
                    <ProfileField
                        label="Navn"
                        value={formatName(user?.fullname)}
                        loading={profileLoading}
                    />
                    <ProfileField
                        label="Mobilnummer"
                        value={formatPhoneNumber(user?.mobile)}
                        loading={profileLoading}
                    />
                    <ProfileField
                        label="E-post"
                        value={toLowerCase(user?.email)}
                        loading={profileLoading}
                    />
                    <ProfileField
                        label="Organisasjon"
                        value={toUpperCase(user?.org)}
                        loading={profileLoading}
                    />
                </dl>
            </section>

            <section className="profile-section">
                <h4 className="profile-section__heading">Nødkontakt</h4>
                <dl className="profile-list">
                    <ProfileField
                        label="Navn"
                        value={formatName(user?.nok_name)}
                        loading={profileLoading}
                    />
                    <ProfileField
                        label="Mobilnummer"
                        value={formatPhoneNumber(user?.nok_phone)}
                        loading={profileLoading}
                    />
                    <ProfileField
                        label="Relasjon"
                        value={capitalizeFirstLetter(user?.nok_relation)}
                        loading={profileLoading}
                    />
                </dl>

                <InfoBox variant="info-muted">
                    <p>
                        Informasjonen er kun tilgjengelig for arrangørene og brukes kun i
                        nødsituasjoner under arrangementet.
                    </p>
                </InfoBox>
            </section>

            <section className="profile-section">
                <h4 className="profile-section__heading">Konto og databehandling</h4>
                <dl className="profile-list">
                    <ProfileField
                        label="Innlogging"
                        value={`SMS-kode${user?.mobile ? ' til ' + formatPhoneNumber(user.mobile) : ''}`}
                        loading={profileLoading}
                    />

                    <ProfileLink
                        label="Behandling av data"
                        href="/personvern"
                        text={
                            <span>
                                Les personvernerklæringen{' '}
                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="sm" />
                            </span>
                        }
                        external
                        loading={profileLoading}
                    />

                    <br />

                    <InfoBox variant="info-muted">
                        <p>
                            Trenger du å oppdatere informasjon vi har om deg, eller ønsker du å
                            melde deg av hele arrangementet? Send oss en melding via{' '}
                            <a href="/om/#kontakt" target="_blank" rel="noopener noreferrer">
                                kontaktskjemaet{' '}
                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="sm" />
                            </a>
                            , så hjelper vi deg.
                        </p>
                    </InfoBox>
                </dl>

                <button
                    type="button"
                    className="btn-outline btn-small mobile-only"
                    onClick={handleLogout}
                >
                    Logg ut
                </button>
            </section>
        </div>
    );
}

export default ProfileTab;
